import { objectService } from "@/api/services/object.service";
import * as Sentry from "@sentry/tanstackstart-react";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

const listMediaSchema = z
  .object({
    cursor: z.number().optional().default(0),
    limit: z.number().optional(),
    type: z.string().optional(),
    search: z.string().optional(),
    sortBy: z.string().optional(),
  })
  .optional();

export const listMediaFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(listMediaSchema)
  .handler(async ({ context, data }) => {
    return await Sentry.startSpan(
      { name: "action.listMediaFn", op: "function" },
      async () => {
        if (context.auth.role !== "admin") {
          throw new Error("Forbidden");
        }

        const result = await Sentry.startSpan(
          { name: "objectService.listObjects", op: "db" },
          () =>
            objectService.listObjects({
              prefix: "",
              limit: data?.limit,
              offset: data?.cursor ?? 0,
              type: data?.type,
              search: data?.search,
              sortBy: data?.sortBy,
            }),
        );

        return {
          status: 200,
          body: result,
        };
      },
    );
  });

export const listMediaOptions = () =>
  queryOptions({
    queryKey: ["listMedia"],
    queryFn: () => listMediaFn(),
    staleTime: 0, // Always fetch fresh data
  });

export const listMediaInfiniteOptions = (filters?: {
  type?: string;
  search?: string;
  sortBy?: string;
}) =>
  infiniteQueryOptions({
    queryKey: ["listMediaInfinite", filters],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await listMediaFn({
        data: {
          cursor: pageParam as number,
          limit: 24,
          type: filters?.type,
          search: filters?.search,
          sortBy: filters?.sortBy,
        },
      });
      return res.body;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage?.nextCursor ?? undefined;
    },
    staleTime: 0,
  });

const listPublicMediaSchema = z
  .object({
    cursor: z.number().optional().default(0),
    limit: z.number().optional(),
    search: z.string().optional(),
  })
  .optional();

export const listPublicMediaFn = createServerFn({ method: "GET" })
  .inputValidator(listPublicMediaSchema)
  .handler(async ({ data }) => {
    return await Sentry.startSpan(
      { name: "action.listPublicMediaFn", op: "function" },
      async () => {
        const result = await Sentry.startSpan(
          { name: "objectService.listObjects", op: "db" },
          () =>
            objectService.listObjects({
              prefix: "",
              limit: data?.limit,
              offset: data?.cursor ?? 0,
              search: data?.search,
              type: "image",
            }),
        );

        return {
          status: 200,
          body: result,
        };
      },
    );
  });

export const listPublicMediaOptions = () =>
  queryOptions({
    queryKey: ["listPublicMedia"],
    queryFn: () => listPublicMediaFn(),
  });

export const listPublicMediaInfiniteOptions = (filters?: {
  search?: string;
}) =>
  infiniteQueryOptions({
    queryKey: ["listPublicMediaInfinite", filters],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await listPublicMediaFn({
        data: {
          cursor: pageParam as number,
          limit: 20,
          search: filters?.search,
        },
      });
      return res.body;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage?.nextCursor ?? undefined;
    },
  });
